import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, documentsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Search, FileText, Download, Eye, X, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_OPTIONS = [
  'Cadastro', 'Envio do documento', 'Validacao do documento',
  'Assinatura do Termo', 'Envio para protocolo', 'Acao Protocolada', 'Remocao das negativacoes'
];

export default function AdminClientReport() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ status: '', termStatus: '', docsStatus: '', stage: '' });
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  // Side panel
  const [panelClient, setPanelClient] = useState(null);
  const [panelFiles, setPanelFiles] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const navigate = useNavigate();

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.clientReport({ page, limit: 25, q, sort, order, ...filters });
      setData(res.data);
    } catch (err) {
      toast.error('Erro ao carregar relatorio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [page, q, sort, order, filters]);

  const openFilesPanel = async (row) => {
    setPanelClient(row);
    setPanelLoading(true);
    try {
      const res = await adminAPI.clientFiles(row.clientId);
      setPanelFiles(res.data);
    } catch {
      toast.error('Erro ao carregar arquivos');
    } finally {
      setPanelLoading(false);
    }
  };

  const closePanel = () => { setPanelClient(null); setPanelFiles(null); };

  const statusBadge = (val) => {
    const map = {
      'CONCLUIDO': 'badge-success', 'EM_ANDAMENTO': 'badge-warning', 'PENDENTE': 'badge-neutral',
      'ASSINADO': 'badge-success', 'AGUARDANDO_ASSINATURA': 'badge-warning', 'NAO_GERADO': 'badge-neutral',
      'APROVADO': 'badge-success', 'ENVIADO': 'badge-success', 'PARCIAL': 'badge-warning', 'REPROVADO': 'badge-danger',
    };
    const labels = {
      'CONCLUIDO': 'Concluido', 'EM_ANDAMENTO': 'Em andamento', 'PENDENTE': 'Pendente',
      'ASSINADO': 'Assinado', 'AGUARDANDO_ASSINATURA': 'Aguardando', 'NAO_GERADO': 'Nao gerado',
      'APROVADO': 'Enviado', 'ENVIADO': 'Enviado', 'PARCIAL': 'Parcial', 'REPROVADO': 'Reprovado',
    };
    return <span className={`${map[val] || 'badge-neutral'} text-[10px]`}>{labels[val] || val}</span>;
  };

  const stepBadge = (val) => val === 'CONCLUIDO'
    ? <span className="badge-success text-[10px]">OK</span>
    : <span className="badge-neutral text-[10px]">-</span>;

  return (
    <div className="space-y-5" data-testid="admin-client-report">
      <div>
        <h1 className="font-display text-2xl font-semibold">Relatorio de Clientes</h1>
        <p className="text-sm text-white/50">{data.total} cliente(s) encontrado(s)</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar nome, CPF, email, WhatsApp..."
            className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]" />
        </div>
        <FilterSelect label="Status" value={filters.status} onChange={v => { setFilters(f => ({...f, status: v})); setPage(1); }}
          options={[{v:'',l:'Todos'},{v:'PENDENTE',l:'Pendente'},{v:'EM_ANDAMENTO',l:'Em andamento'},{v:'CONCLUIDO',l:'Concluido'}]} />
        <FilterSelect label="Termo" value={filters.termStatus} onChange={v => { setFilters(f => ({...f, termStatus: v})); setPage(1); }}
          options={[{v:'',l:'Todos'},{v:'ASSINADO',l:'Assinado'},{v:'NAO_GERADO',l:'Nao gerado'}]} />
        <FilterSelect label="Docs" value={filters.docsStatus} onChange={v => { setFilters(f => ({...f, docsStatus: v})); setPage(1); }}
          options={[{v:'',l:'Todos'},{v:'APROVADO',l:'Aprovado'},{v:'PENDENTE',l:'Pendente'},{v:'REPROVADO',l:'Reprovado'}]} />
        <FilterSelect label="Ordenar" value={sort} onChange={v => setSort(v)}
          options={[{v:'createdAt',l:'Data cadastro'},{v:'lastUpdateAt',l:'Ultima atualizacao'},{v:'nome',l:'Nome'}]} />
      </div>

      {/* Table */}
      <div className="glass-card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="rn-spinner" style={{width:28,height:28}} /></div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={36} className="mx-auto text-white/15 mb-2" />
            <p className="text-sm text-white/35">Nenhum cliente encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02]">
                  <th className="text-left text-[11px] text-white/40 font-medium py-3 px-4">Cliente</th>
                  <th className="text-left text-[11px] text-white/40 font-medium py-3 px-3">Processo</th>
                  <th className="text-left text-[11px] text-white/40 font-medium py-3 px-3">Docs</th>
                  <th className="text-left text-[11px] text-white/40 font-medium py-3 px-3">Termo</th>
                  <th className="text-left text-[11px] text-white/40 font-medium py-3 px-3">Etapas</th>
                  <th className="text-center text-[11px] text-white/40 font-medium py-3 px-3">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(row => (
                  <tr key={row.clientId} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    {/* Cliente */}
                    <td className="py-3 px-4">
                      <p className="font-medium text-white/90 text-sm">{row.nome}</p>
                      <p className="text-[11px] font-mono text-white/50">{row.cpf}</p>
                      <p className="text-[10px] text-white/30 truncate max-w-[180px]">{row.email}</p>
                      <p className="text-[10px] text-white/30">{row.whatsapp}</p>
                    </td>
                    {/* Processo */}
                    <td className="py-3 px-3">
                      <div className="flex flex-col gap-1">
                        {statusBadge(row.statusGeral)}
                        <span className="text-[10px] text-white/45">{row.currentStage}</span>
                        <span className="text-[10px] text-white/25">{row.lastUpdateAt ? new Date(row.lastUpdateAt).toLocaleDateString('pt-BR') : '-'}</span>
                      </div>
                    </td>
                    {/* Docs */}
                    <td className="py-3 px-3">
                      <div className="flex flex-col gap-1">
                        {statusBadge(row.docsStatus)}
                        <span className="text-[10px] text-white/45">{row.docsOk}/{row.docsTotal || 0}</span>
                      </div>
                    </td>
                    {/* Termo */}
                    <td className="py-3 px-3">
                      <div className="flex flex-col gap-1">
                        {statusBadge(row.termStatus)}
                        {row.termSignedAt && <span className="text-[10px] text-white/30">{new Date(row.termSignedAt).toLocaleDateString('pt-BR')}</span>}
                        {row.termStatus === 'ASSINADO' && row.termPdfKey && (
                          <a href={`${BACKEND_URL}/api/cases/${row.caseId}/term/pdf`} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] flex items-center gap-1 hover:underline" style={{color:'var(--rn-green)'}}>
                            <Download size={10} /> PDF
                          </a>
                        )}
                      </div>
                    </td>
                    {/* Etapas */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1.5">
                        <div className="flex flex-col items-center gap-0.5" title="Protocolo">
                          <span className="text-[8px] text-white/25">Prot</span>
                          {stepBadge(row.stepProtocol)}
                        </div>
                        <div className="flex flex-col items-center gap-0.5" title="Acao">
                          <span className="text-[8px] text-white/25">Acao</span>
                          {stepBadge(row.stepAction)}
                        </div>
                        <div className="flex flex-col items-center gap-0.5" title="Baixas">
                          <span className="text-[8px] text-white/25">Baixas</span>
                          <span className={`text-[10px] font-mono ${row.negCompleted >= 4 ? 'text-[var(--rn-green)]' : 'text-white/40'}`}>
                            {row.negCompleted}/{row.negTotal}
                          </span>
                        </div>
                      </div>
                    </td>
                    {/* Acoes */}
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openFilesPanel(row)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" title="Ver arquivos">
                          <Eye size={14} style={{color:'var(--rn-green)'}} />
                        </button>
                        {row.caseId && (
                          <button onClick={() => navigate(`/admin/cases/${row.caseId}`)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" title="Abrir detalhes">
                            <FileText size={14} className="text-white/50" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
            <span className="text-xs text-white/35">Pagina {data.page} de {data.pages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Anterior</button>
              <button onClick={() => setPage(p => Math.min(data.pages, p+1))} disabled={page === data.pages} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Proximo</button>
            </div>
          </div>
        )}
      </div>

      {/* === SIDE PANEL (FILES) === */}
      {panelClient && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={closePanel}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-md h-full overflow-y-auto" style={{background:'var(--rn-bg)',borderLeft:'1px solid var(--rn-glass-border)'}}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-white/10" style={{background:'rgba(11,15,16,0.95)',backdropFilter:'blur(12px)'}}>
              <div>
                <h3 className="font-display text-base font-semibold">{panelClient.nome}</h3>
                <p className="text-xs text-white/40 font-mono">{panelClient.cpf}</p>
              </div>
              <button onClick={closePanel} className="p-2 rounded-xl hover:bg-white/10"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-5">
              {panelLoading ? (
                <div className="flex justify-center py-8"><div className="rn-spinner" style={{width:24,height:24}} /></div>
              ) : (
                <>
                  {/* Documents */}
                  <div>
                    <h4 className="text-sm font-semibold text-white/70 mb-3">Documentos ({panelFiles?.documents?.length || 0})</h4>
                    {(!panelFiles?.documents || panelFiles.documents.length === 0) ? (
                      <p className="text-xs text-white/30">Nenhum documento enviado.</p>
                    ) : (
                      <div className="space-y-2">
                        {panelFiles.documents.map(doc => (
                          <div key={doc.id} className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02]">
                            <FileText size={16} className="text-white/30 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{doc.fileName}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                                  doc.status === 'validado' ? 'badge-success' : doc.status === 'divergente' ? 'badge-danger' : 'badge-warning'
                                }`}>
                                  {doc.status === 'validado' ? 'Validado' : doc.status === 'divergente' ? 'Divergente' : 'Recebido'}
                                </span>
                              </div>
                            </div>
                            <a href={documentsAPI.getFileUrl(doc.id)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-white/5">
                              <Download size={14} style={{color:'var(--rn-green)'}} />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Term */}
                  <div>
                    <h4 className="text-sm font-semibold text-white/70 mb-3">Termo de Filiacao</h4>
                    {panelFiles?.term ? (
                      <div className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02]">
                        <FileText size={16} style={{color:'var(--rn-green)'}} />
                        <div className="flex-1">
                          <p className="text-xs font-medium">Termo de Filiacao</p>
                          <span className="badge-success text-[9px]">Assinado</span>
                          {panelFiles.term.signedAt && (
                            <p className="text-[10px] text-white/30 mt-0.5">
                              {new Date(panelFiles.term.signedAt).toLocaleDateString('pt-BR', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                            </p>
                          )}
                        </div>
                        {panelFiles.term.fileKey && panelFiles.caseId && (
                          <a href={`${BACKEND_URL}/api/cases/${panelFiles.caseId}/term/pdf`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-white/5">
                            <Download size={14} style={{color:'var(--rn-green)'}} />
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-white/30">Termo nao assinado.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="min-w-[120px]">
      <label className="text-[10px] text-white/30 mb-1 block">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-black/20 border border-white/10 text-white text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
